import computeInterviewPayload from '@Application/Beneficiaries/Interviews/Actions/computeInterviewPayload';
import type { InterviewConsentPayload } from '@Application/Beneficiaries/Interviews/Actions/interviewConsentFetched';
import type { TranscriptionIssuePayload } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';
import type { Computed } from '@Domain/Computed';
import type PlainDate from '@Domain/PlainDate';

/**
 * Seven fields plus `facility`. `durationMs` is `null` until finalisation and remains a valid value
 * afterwards — the recorded time, pauses excluded (measured by the capture clock for a recording,
 * probed from the file for an import). `source`
 * says how the bytes were produced: captured live in the application, or imported from an existing
 * file. It is not a status and never decides whether work remains on the recording — readers use
 * `status.value` for that.
 */
export type RecordingPayload = {
	readonly id: number;
	readonly facility: { readonly id: number };
	readonly status: LabeledValuePayload;
	readonly source: LabeledValuePayload;
	readonly mimeType: string | null;
	readonly durationMs: number | null;
	readonly segmentCount: number;
	readonly byteSize: number;
	readonly completedAt: string | null;
};

/**
 * Neither `conductedBy`, nor `beneficiary`, nor technical timestamps
 * ever come out of this route. `facility` is declared even though `InterviewView` never copies it:
 * it is present in the payload (`FacilityScopedEntity.facility` carries the `id` group), and a
 * contract test would be the first place this omission would otherwise surface. `list`, `get` and
 * `create` all emit the exact same shape: `recording` and `consent` are the full nested object or
 * `null`, never a reference and never optional — carrying an `{ id }` reference here would force a
 * second round trip to read a status or a withdrawal date the server already sent.
 * `participantContacts`/`participantStaffMembers` follow the opposite, general rule: a relation
 * comes out by its identifiers and nothing else, so each entry is a bare `{ id }` — the front resolves
 * the name itself from the referential it already loads. They are always present, never `null`, and
 * empty rather than omitted when an interview has no participant on that side. `clientReference`
 * is `null` only on a row written before this field existed; it is the deduplication key between a
 * local interview and its server counterpart (`UI/Helpers/helpers/mergeInterviewEntries`).
 * `name` is `null` only on a row predating it; `kind` is never null — an enum, always `{ value, label }`,
 * never a bare string, and never typed by the user: it is decided by which capture button was
 * clicked.
 * `processingState` is the server's reading of where the interview stands against its treatment, `{ value, label }`.
 */
export type InterviewPayload = {
	readonly id: number;
	readonly facility: { readonly id: number };
	readonly clientReference: string | null;
	readonly name: string | null;
	readonly kind: LabeledValuePayload;
	readonly conductedOn: string;
	readonly participantContacts: ReadonlyArray<{ readonly id: number }>;
	readonly participantStaffMembers: ReadonlyArray<{ readonly id: number }>;
	readonly recording: RecordingPayload | null;
	readonly consent: InterviewConsentPayload | null;
	readonly processingState: LabeledValuePayload;
	/** The most recent preparation containing the interview, resolved server-side. */
	readonly preparation: { readonly id: number } | null;
	readonly transcriptionIssue: TranscriptionIssuePayload | null;
};

export type ComputedInterviewPayload = Computed<InterviewPayload, { conductedOn: PlainDate | null }>;

export type InterviewsFetchedPayload = {
	totalRecords: number;
	results: ComputedInterviewPayload[];
};

export type InterviewsFetchedAction = {
	type: 'INTERVIEWS_FETCHED';
	payload: InterviewsFetchedPayload;
	facilityId: number;
	subjectId: number;
	typeKey: string;
};

export default (
	payload: { totalRecords: number; results: InterviewPayload[] },
	facilityId: number,
	subjectId: number,
	typeKey: string,
): InterviewsFetchedAction => ({
	type: 'INTERVIEWS_FETCHED',
	payload: {
		totalRecords: payload.totalRecords,
		results: payload.results.map((interview) => computeInterviewPayload(interview)),
	},
	facilityId,
	subjectId,
	typeKey,
});
